/**
 * The label an icon-only console control shows on hover and on keyboard focus
 * (PRD-0290 D-7).
 *
 * A `title` attribute is the thing this replaces: it never appears on keyboard
 * focus, its delay and styling belong to the operating system, and a touch
 * pointer never sees it at all. Radix supplies the rest of the contract —
 * pointer types other than touch, `aria-describedby` while open, dismissal on
 * Escape through its document listener, and hoverable content — from one
 * provider mounted for the whole application in `src/App.tsx`.
 */

import { useEffect, useState, type ReactElement } from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { LAYER } from '@/theme/layers'

/**
 * The pause before the first hint of a group opens, and the window in which a
 * neighbouring hint opens without paying it again.
 *
 * Declared beside the component rather than at the provider so the application
 * and the test helpers that stand in for it cannot drift apart: the "recently
 * open" state `skipDelayDuration` drives lives per provider, so the two values
 * only mean what they say while exactly one provider carries both.
 */
export const HINT_DELAY_MS = 150
export const HINT_SKIP_DELAY_MS = 300

export interface ConsoleHintProps {
  /** The visible label; also what assistive tech reads through aria-describedby. */
  label: string
  /** Rendered as a <kbd> after the label, e.g. '⌘ \\'. */
  shortcut?: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** A control whose label is already visible (an expanded rail) shows no hint. */
  disabled?: boolean
  /**
   * One focusable element. Radix's asChild clones the trigger props onto it,
   * so it must forward its ref: a native element, NavLink or any forwardRef
   * component qualifies; a plain function component compiles but drops the
   * ref at runtime, which TypeScript cannot express.
   */
  children: ReactElement
}

export const HINT_CONTENT_CLASS = `${LAYER.tooltip} max-w-[240px] rounded-md border border-ui-hairline bg-ui-surface px-2.5 py-1.5 font-sans text-meta text-ui-text shadow-lg shadow-black/40 forced-colors:border-[CanvasText]`

export function ConsoleHint({
  label,
  shortcut,
  side = 'top',
  disabled = false,
  children,
}: ConsoleHintProps) {
  // Always controlled: Radix warns when a root switches between controlled and
  // uncontrolled, and the rail flips `disabled` on every expand and collapse.
  const [open, setOpen] = useState(false)

  // Forcing the root's value to false hides an open hint but does not tell the
  // state it is shut, and a controlled prop change fires no onOpenChange. The
  // remembered `true` would reopen the hint the moment the rail collapsed, so
  // it is discarded as `disabled` goes true rather than only masked.
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  // Triggers still emit hover/focus requests while their content is disabled.
  // Reject those requests so collapsing the rail cannot reveal hidden hints.
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(disabled ? false : nextOpen)
  }

  return (
    <TooltipPrimitive.Root
      open={disabled ? false : open}
      onOpenChange={handleOpenChange}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={8}
          collisionPadding={8}
          className={HINT_CONTENT_CLASS}
        >
          {label}
          {shortcut && (
            <kbd className="ml-2 font-mono text-meta text-ui-text-muted">
              {shortcut}
            </kbd>
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
